import React, {memo, useCallback, useEffect, useMemo, useState} from 'react';
import {PasswordContainer} from '../../../../components/PasswordContainer';
import {SettingsPageContainer} from '../../../../components/Settings/SettingsPageContainer';
import {useHistory, useParams} from 'react-router-dom';
import {useQuestion} from '../../../../hooks/question';
import {useKeyboard} from '../../../../hooks/keyboard';
import {SettingInputComponent} from '../../../../components/Settings/SettingInputComponent';
import {AreaModel} from '../../../../store/control/state';
import {SettingSelectComponent} from '../../../../components/Settings/SettingSelectComponent';
import {SelectItem} from '../../../../components/Select';
import {useFilterSelector} from '../../../../hooks/filter-selector';
import {useMessage} from '../../../../hooks/message';
import {useRPC} from '../../../../hooks/rpc';
import {useLocalization} from "../../../../hooks/localization";

export const RoomEditScreen = memo(function RoomEditScreen() {
    const {t} = useLocalization();
    const params = useParams<{ id: string; floor_id: string; }>();
    const {success, failure} = useMessage();
    const question = useQuestion();
    const [setActiveInput, renderKeyboard, currentInput] = useKeyboard();
    const [rpc] = useRPC();
    const history = useHistory();

    const floorList = useFilterSelector(state => state.control.areaList, area => area.type === 'floor', []);
    const roomList = useFilterSelector(state => state.control.areaList, area => area.type === 'room', []);

    const [selected, setSelected] = useState<AreaModel>(null);
    const [backURL, setBackURL] = useState('/settings/project-information');
    const [name, setName] = useState('');
    const [floor, setFloor] = useState<SelectItem<AreaModel>>(null);

    const floorOptionList: SelectItem<AreaModel>[] =
        useMemo(() => floorList.map(floor => ({value: floor, label: floor.name})), [floorList]);

    const onBackPressed = useCallback(() => {
        history.push(backURL);
    }, [history, backURL]);

    const isFieldsValid = useCallback((): boolean => {
        if (name == null || name.length === 0) {
            failure(t('pls_enter_a_valid_name'));

            return false;
        }

        if (floor == null || floor.value == null) {
            failure(t('pls_select_floor'));

            return false;
        }

        return true;
    }, [t, failure, floor, name]);

    const onSavePressed = useCallback(() => {
        if (!isFieldsValid()) {
            return;
        }

        const areaModel: AreaModel = {
            id: selected != null ? selected.id : null,
            name,
            type: 'room',
            parent_area_id: floor.value.id,
        };

        return rpc.updateArea(areaModel)
            .then(() => {
                success(t(areaModel.id == null ? 'room_successfully_created' : 'room_successfully_edited'));
                onBackPressed();
            }).catch(failure);
    }, [
        t,
        selected,
        isFieldsValid,
        name,
        floor,
        success,
        failure,
        onBackPressed,
        rpc,
    ]);

    const onDeletePressed = useCallback(() => {
        question(t('are_you_sure_to_delete_the_room'), () => {
            return rpc.deleteArea(selected.id)
                .then(() => {
                    success(t('room_successfully_deleted'));
                    onBackPressed();
                })
                .catch(failure);
        });
    }, [t, failure, onBackPressed, question, rpc, selected, success]);

    const buttons = useMemo(() =>
        [
            {
                text: t('delete'),
                className: 'mulberry-wood',
                onClick: onDeletePressed,
                hidden: !(selected),
            },
            {
                text: t('save'),
                className: 'green',
                onClick: onSavePressed,
            },
            {
                text: t('cancel'),
                className: 'red',
                url: backURL,
            },
        ], [t, onDeletePressed, selected, onSavePressed, backURL],
    );

    const onBlur = useCallback(() => {
        setActiveInput(null);
    }, [setActiveInput]);

    useEffect(() => {
        if (params.floor_id != null) {
            const floor = floorList.find(floor => floor.id === params.floor_id);
            if (floor) {
                setBackURL(`/settings/floor/${floor.id}/room`);
            }
            setFloor(floorOptionList.find(floorOption => floorOption.value.id === floor.id));
        }

        if (params.id == null) {
            return;
        }

        const room = roomList.find(room => room.id === params['id']);
        if (room == null) {
            return;
        }

        setSelected(room);
        setName(room.name);
    }, [floorList, floorOptionList, params, roomList]);

    return (
        <PasswordContainer requiredRole='service' isFullscreen={true}>
            <SettingsPageContainer
                className='service-edit'
                buttons={buttons}
                backUrl={backURL}
                currentInput={currentInput}>
                <div className='content-divider'>
                    <div className='content-wrapper'>
                        <SettingInputComponent
                            placeholder={t('write_room_name')}
                            label={t('room_name')}
                            value={name}
                            setValue={setName}
                            setActiveInput={setActiveInput}
                            onBlur={onBlur}/>
                        <SettingSelectComponent
                            label={t('floor')}
                            options={floorOptionList}
                            onChange={setFloor}
                            value={floor}/>
                    </div>
                    <div className='content-wrapper'>

                    </div>
                </div>
            </SettingsPageContainer>
            {
                renderKeyboard()
            }
        </PasswordContainer>
    );
});
